/**
 * Authentication Context
 * Provides user state, login/logout methods, and binding status
 *
 * Local dev: Uses localStorage flag + special header for auth bypass
 * Production: Uses EasyAuth, fetches real binding status from API
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import type { UserProfile, Role } from 'blia-events-shared';
import { api } from '../services/api';
import { getEasyAuthInfo } from '../services/authService';
import { isDevelopment } from '../utils/isDevelopment';
import { safeReturnTo } from '../utils/safeReturnTo';

const DEV_AUTH_KEY = 'dev_authenticated';
// Session flag to track explicit login intent - prevents auto-login from browser SSO
const EXPLICIT_LOGIN_KEY = 'explicit_login';

interface AuthContextType {
  user: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isBound: boolean;
  error: string | null;
  login: (provider?: 'aad' | 'google', returnTo?: string) => void;
  /** Recognise an EasyAuth session this tab did not start (e.g. a QR opened in a fresh tab). */
  adoptExistingSession: () => Promise<boolean>;
  /** `returnTo`: same-origin path to land on after sign-out (default `/`). */
  logout: (returnTo?: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  /** Re-read /auth/me without touching isLoading (no app-wide loader, no remount). */
  refreshUserQuietly: () => Promise<void>;
  hasRole: (role: Role) => boolean;
  hasRoleAtLeast: (minimumRole: Role) => boolean;
  canAccessAssociation: (associationId: string) => boolean;
  canAdminAssociation: (associationId: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ROLE_HIERARCHY: Record<Role, number> = {
  SysAdmin: 100,
  OrgAdmin: 50,
  Member: 10,
  Guest: 0,
};

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const userRef = useRef<UserProfile | null>(null);
  userRef.current = user;

  const fetchUser = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // In dev mode, check if user has "logged in" via localStorage
      if (isDevelopment()) {
        const isDevAuthenticated = localStorage.getItem(DEV_AUTH_KEY) === 'true';
        if (!isDevAuthenticated) {
          // Not logged in - show login page
          setUser(null);
          setIsLoading(false);
          return;
        }
      } else {
        // Production mode: Only fetch user if they have explicitly clicked login
        // This prevents auto-authentication from browser SSO sessions
        const hasExplicitLogin = sessionStorage.getItem(EXPLICIT_LOGIN_KEY) === 'true';
        if (!hasExplicitLogin) {
          // User hasn't clicked login - show landing page
          setUser(null);
          setIsLoading(false);
          return;
        }
      }

      // Fetch real user profile from API
      // In dev mode: API will auto-auth with dev user and fetch from DB
      // In production: API uses EasyAuth header
      const response = await api.get<{ data: UserProfile }>('/auth/me');
      setUser(response.data.data);
    } catch (err) {
      // Not authenticated (401) - this is expected, show login page
      setUser(null);
      // Clear auth flags if API returns 401
      if (isDevelopment()) {
        localStorage.removeItem(DEV_AUTH_KEY);
      } else {
        sessionStorage.removeItem(EXPLICIT_LOGIN_KEY);
      }
      // Only set error for non-auth errors
      if (err instanceof Error && !err.message.includes('401') && !err.message.includes('Authentication')) {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = useCallback((provider: 'aad' | 'google' = 'aad', returnTo?: string) => {
    if (isDevelopment()) {
      // In dev mode, just set the localStorage flag and reload
      // The API will auto-authenticate with the dev user
      localStorage.setItem(DEV_AUTH_KEY, 'true');
      window.location.href = '/';
      return;
    }

    // Production: Set explicit login flag before redirecting to EasyAuth
    // This flag tells fetchUser() that the user intentionally clicked login
    sessionStorage.setItem(EXPLICIT_LOGIN_KEY, 'true');

    // Redirect to EasyAuth login
    const loginPath = provider === 'google' ? '/.auth/login/google' : '/.auth/login/aad';
    const safe = safeReturnTo(returnTo);
    if (safe) {
      const callback = '/login/callback?post_login_redirect_uri=' + encodeURIComponent(safe);
      window.location.href = `${loginPath}?post_login_redirect_uri=${encodeURIComponent(callback)}`;
      return;
    }
    window.location.href = loginPath;
  }, []);

  const logout = useCallback(async (returnTo?: string) => {
    // Clear user state
    setUser(null);

    if (isDevelopment()) {
      // In dev mode, clear the localStorage flag
      localStorage.removeItem(DEV_AUTH_KEY);
      window.location.href = safeReturnTo(returnTo) ?? '/';
      return;
    }

    // Production: Clear explicit login flag first to prevent auto-re-login
    sessionStorage.removeItem(EXPLICIT_LOGIN_KEY);

    // Call logout API and redirect to EasyAuth logout
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore logout errors
    }
    // Redirect after EasyAuth logout: the caller's page when it is a safe path, else the landing page
    window.location.href = '/.auth/logout?post_logout_redirect_uri=' + encodeURIComponent(safeReturnTo(returnTo) ?? '/');
  }, []);

  const refreshUser = useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  // Quiet on purpose: never touches isLoading (App swaps <Routes> for a loader
  // while it is true, which would remount the caller and loop).
  const adoptExistingSession = useCallback(async (): Promise<boolean> => {
    if (isDevelopment()) return userRef.current !== null;
    try {
      const info = await getEasyAuthInfo();
      if (!info.clientPrincipal) return false;
      sessionStorage.setItem(EXPLICIT_LOGIN_KEY, 'true');
      const response = await api.get<{ data: UserProfile }>('/auth/me');
      setUser(response.data.data);
      return true;
    } catch {
      return false;
    }
  }, []);

  const refreshUserQuietly = useCallback(async () => {
    try {
      const response = await api.get<{ data: UserProfile }>('/auth/me');
      setUser(response.data.data);
    } catch {
      // Keep the current profile; the caller navigates on regardless.
    }
  }, []);

  const hasRole = useCallback((role: Role): boolean => {
    return user?.role === role;
  }, [user]);

  const hasRoleAtLeast = useCallback((minimumRole: Role): boolean => {
    if (!user) return false;
    return ROLE_HIERARCHY[user.role] >= ROLE_HIERARCHY[minimumRole];
  }, [user]);

  const canAccessAssociation = useCallback((associationId: string): boolean => {
    if (!user) return false;
    if (user.role === 'SysAdmin') return true;
    return user.associations.some(a => a.associationId === associationId);
  }, [user]);

  const canAdminAssociation = useCallback((associationId: string): boolean => {
    if (!user) return false;
    if (user.role === 'SysAdmin') return true;
    return user.associations.some(a => a.associationId === associationId && a.isAdmin);
  }, [user]);

  const isAuthenticated = user !== null;
  const isBound = user?.bindingStatus === 'bound';

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    isBound,
    error,
    login,
    adoptExistingSession,
    logout,
    refreshUser,
    refreshUserQuietly,
    hasRole,
    hasRoleAtLeast,
    canAccessAssociation,
    canAdminAssociation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthContext };
